<script>
  import { setContext, getContext, tick, onMount, untrack } from "svelte";
  import { ENTRIES } from "./app_config.js";
  import { page } from '$app/state';
  import t from "#lib/i18n.svelte.js";
  import { fetch_json, entry_url, ACCEPTED_IMAGE_FILE_TYPES } from "#lib/util.js";
  import { goto, refreshAll, beforeNavigate } from '$app/navigation';
  import { add_media, is_busy as uploads_busy, wait_for_idle as wait_for_uploads } from '#lib/upload_manager.svelte.js';
  import { read_blocks_from_clipboard } from '#lib/block_clipboard.js';
  import { toast } from '#lib/toast.svelte.js';
  import MainPageContent from "#lib/MainPageContent.svelte";
  import ContextualEditingDrawer from "#lib/ContextualEditingDrawer.svelte";
  import { lazy } from '#lib/lazy.svelte.js';
  // Dialogs behind admin/member actions - split out of the reader bundle, loaded on first open
  const entry_picker_drawer = lazy(() => import('#lib/EntryPickerDrawer.svelte'));
  const add_to_collection_dialog = lazy(() => import('#lib/AddToCollectionDialog.svelte'));
  const series_save_dialog = lazy(() => import('#lib/SeriesSaveDialog.svelte'));
  import Footer from "#lib/Footer.svelte";
  import SEO from "#lib/SEO.svelte";

  let { raw_entry, is_create_mode, is_homepage, additional_entry_data } = $props();

  function create_entry_from_raw_entry() {
    const entry_type = raw_entry?.type;
    const entry_config = ENTRIES[entry_type];

    if (!entry_config?.model) {
      const supported_entry_types = Object.keys(ENTRIES).join(', ');
      throw new Error(
        `Unsupported entry type "${entry_type}" for entry "${raw_entry?.entry_id || 'unknown'}". Supported types: ${supported_entry_types}`
      );
    }

    return new entry_config.model(raw_entry);
  }

  function should_start_in_edit_mode() {
    return is_create_mode || Boolean(page.url.searchParams.get('edit'));
  }

  // This is "sort of" considered derived state initially (see effect)
  let entry = $state(create_entry_from_raw_entry());
  let site_settings = getContext('site_settings');

  let first_block_background_color = $derived(entry?.body?.blocks?.[0]?.background_color);
  let is_editing = $state(should_start_in_edit_mode());
  let add_to_collection_dialog_open = $state(false);
  let active_container = $state(undefined);
  let contextual_entry_id = $state();
  let contextual_editing_callbacks = $state({ onsave: null, oncancel: null });

  // Entry picker state
  let entry_picker_open = $state(false);
  let entry_picker_options = $state({
    entry_type: '',
    initial_entry_type: '',
    initial_selected: [],
    excluded_ids: [],
    onconfirm: null,
    oncancel: null,
  });

  // Canvas toolbar menu state (for coordinating visibility between block and embed toolbars)
  // Values: null (none open), 'block', 'embed'
  let active_canvas_menu = $state(null);

  // Series post-save dialog state (all logic lives in SeriesSaveDialog.svelte)
  let series_dialog_entry = $state(null);
  let series_dialog_impact_count = $state(0);

  // Processing state for save/cancel operations (prevents double-clicks)
  const PROCESSING_INDICATOR_DELAY_MS = 200;
  let is_processing = $state(false);
  let show_processing = $state(false);
  let processing_timer = null;

  /**
   * Start processing state with delayed indicator
   */
  function start_processing() {
    is_processing = true;
    processing_timer = setTimeout(() => {
      if (is_processing) show_processing = true;
    }, PROCESSING_INDICATOR_DELAY_MS);
  }

  /**
   * End processing state and clear indicator
   */
  function end_processing() {
    is_processing = false;
    show_processing = false;
    if (processing_timer) {
      clearTimeout(processing_timer);
      processing_timer = null;
    }
  }

  function get_page_title() {
    if (is_create_mode) {
      return t('new_entry',{ entry_type: t(raw_entry.type) });
    } else if (is_homepage) {
      return site_settings.name;
    } else {
      return `${entry.preview.title || 'Untitled'} — ${site_settings.name}`;
    }
  }

  // Call this (from different) places to reset
  // E.g. when <PlainText> calls this when not provided with an explicit focus property.
  function clear_selection() {
    active_container?.clear_selection();
    active_container = undefined;
  }

  // ——— Draft safety ————————————————————————————————————————————————
  // Snapshot of the last saved state; used for "unsaved changes" checks and to
  // know when the localStorage draft can be dropped.
  let saved_snapshot = $state(null);
  // Autosave stays off until the baseline is captured and a stored draft has been
  // offered: otherwise the fresh entry would overwrite or delete the draft first.
  let draft_ready = $state(false);

  function current_snapshot() {
    try { return JSON.stringify(entry.to_json()); } catch { return null; }
  }

  function has_unsaved_changes() {
    if (!is_editing) return false;
    if (saved_snapshot == null) return is_create_mode;
    return current_snapshot() !== saved_snapshot;
  }

  function draft_key() {
    if (raw_entry?.entry_id) return `entry-draft-${raw_entry.entry_id}`;
    // A duplicate (/new/<type>?from=<id>) must not pick up, or overwrite, the
    // draft of a blank new entry of the same type
    const from = page.url.searchParams.get('from');
    return `entry-draft-new-${raw_entry?.type || 'entry'}${from ? `-from-${from}` : ''}`;
  }

  // The pending debounced write: dropping the draft must cancel it, or it
  // writes the discarded snapshot back a moment later
  let draft_write_timer = null;

  function clear_draft() {
    clearTimeout(draft_write_timer);
    try { localStorage.removeItem(draft_key()); } catch { /* storage unavailable */ }
  }

  /** Offer to restore a crash-orphaned draft when editing starts. */
  function maybe_restore_draft() {
    try {
      const stored = localStorage.getItem(draft_key());
      if (!stored || stored === current_snapshot()) return;
      if (confirm(t('draft_restore_confirm'))) {
        const parsed = JSON.parse(stored);
        entry.dispose?.();
        entry = new ENTRIES[raw_entry.type].model(parsed);
      } else {
        clear_draft();
      }
    } catch (err) {
      console.error('Draft restore failed:', err);
    }
  }

  // Debounced draft snapshot to localStorage while editing — cheap insurance
  // against crashes and power loss while the editor has no undo.
  $effect(() => {
    if (!is_editing || !draft_ready) return;
    const json = current_snapshot();
    if (json == null) return;
    if (saved_snapshot != null && json === saved_snapshot) {
      clear_draft();
      return;
    }
    const timer = setTimeout(() => {
      try { localStorage.setItem(draft_key(), json); } catch { /* storage full/unavailable */ }
    }, 1500);
    draft_write_timer = timer;
    return () => clearTimeout(timer);
  });

  // Baseline first, recovery second, autosave last. The baseline is the clean
  // entry after the editor has rendered (editable bindings normalize undefined
  // fields to '' during mount) — never the recovered draft, which must stay
  // "unsaved" so it keeps its stored copy and the discard warnings.
  async function begin_draft_session() {
    draft_ready = false;
    await tick();
    if (!is_editing) return;
    saved_snapshot = current_snapshot();
    maybe_restore_draft();
    draft_ready = true;
  }

  function start_editing() {
    is_editing = true;
    clear_selection();
    begin_draft_session();
  }

  // Create mode (/new/[type]) and direct ?edit links start in editing without
  // start_editing(), so they need the same draft session.
  onMount(() => {
    if (is_editing) begin_draft_session();
  });

  // ——— Files-first media: paste or drop images anywhere on the page ————————
  /** The selected story block, if any — where pasted/dropped media lands. */
  function get_media_target_block() {
    const selected = entry?.body?.selected_block;
    return selected?.type === 'story' ? selected : null;
  }

  /** Upload files as media entries and place them: selected block, else a new block at the end. */
  async function place_files_as_media(files) {
    if (!files?.length || !entry?.body) return;
    let target = get_media_target_block();
    if (!target) {
      await entry.body.add_block('story', {}, { position: -1 });
      target = entry.body.blocks[entry.body.blocks.length - 1];
      if (!target) return;
    }
    add_media(files, {
      target: target.block_id,
      on_entry: (media_entry) => {
        target.entry_previews_multiref = [...(target.entry_previews_multiref || []), media_entry.entry_id];
        if (media_entry.deduped) toast.info(t('already_in_library'));
      },
    });
  }

  function can_accept_media_files() {
    return is_editing && entry?.body && page.data.current_user?.role === 'admin';
  }

  /**
   * Blocks copied on another page (or in another tab) land here - see block_clipboard.js.
   * @returns {boolean} whether the paste was handled
   */
  function paste_blocks_from_clipboard(event) {
    if (!is_editing || !entry?.body) return false;
    // A cursor inside a text field means the user is after the text, not a block.
    if (document.activeElement?.getAttribute('contenteditable') === 'true') return false;

    const payload = read_blocks_from_clipboard(event.clipboardData?.getData('text/plain'));
    if (!payload) return false;

    event.preventDefault();
    // Every entry model holds exactly one container, so this is the container being edited.
    const dropped_anchor_count = entry.body.paste_blocks(payload.blocks, {
      same_entry: payload.source_entry_id === entry.entry_id,
    });
    if (dropped_anchor_count > 0) toast.info(t('pasted_anchors_removed'));
    return true;
  }

  function handle_window_paste(event) {
    if (can_accept_media_files()) {
      const files = Array.from(event.clipboardData?.files || [])
        .filter(file => ACCEPTED_IMAGE_FILE_TYPES.includes(file.type));
      if (files.length > 0) {
        event.preventDefault();
        place_files_as_media(files);
        return;
      }
    }
    paste_blocks_from_clipboard(event); // plain text paste stays untouched
  }

  function handle_window_dragover(event) {
    if (!can_accept_media_files()) return;
    if (Array.from(event.dataTransfer?.types || []).includes('Files')) {
      event.preventDefault();
    }
  }

  /** Window-level fallback: drops not caught by a block land in the selected block or a new one. */
  function handle_window_drop(event) {
    if (!can_accept_media_files()) return;
    if (event.defaultPrevented) return; // a story block already handled this drop
    const files = Array.from(event.dataTransfer?.files || [])
      .filter(file => ACCEPTED_IMAGE_FILE_TYPES.includes(file.type));
    if (files.length === 0) return;
    event.preventDefault();
    place_files_as_media(files);
  }

  // Warn about a dirty draft on hard unload (in-flight uploads warn from the upload manager)
  function handle_beforeunload(event) {
    if (has_unsaved_changes()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  // In-app navigation: block while uploads are in flight, confirm on a dirty draft
  beforeNavigate(({ cancel, type, shallow }) => {
    // Hard unloads (reload, tab close, HMR full reload) are covered by the native
    // beforeunload dialog — a confirm() on top would prompt twice and block the page.
    // Shallow navigations (a drawer opening) don't leave the entry at all.
    if (type === 'leave' || shallow) return;
    if (uploads_busy()) {
      if (!confirm(t('upload_in_progress_warning'))) cancel();
      return;
    }
    if (has_unsaved_changes()) {
      // Leaving on purpose discards the changes: their draft must not be
      // offered again on the next visit
      if (confirm(t('discard_changes_confirmation'))) clear_draft();
      else cancel();
    }
  });

  /**
   * Called when contextual editing ends (via save or cancel)
   * @param {{ saved: boolean }} options
   */
  function end_contextual_editing({ saved = false } = {}) {
    // Refresh previews that may have changed in the in-place editor.
    const blocks = entry.body?.blocks ?? [];
    for (const block of blocks) {
      if (block.type === 'story') {
        // Reload all entry previews that reference the edited entry
        if (block.entry_previews_multiref.includes(contextual_entry_id)) {
          block.refresh_entry_previews();
        }
      }
    }

    // Call the appropriate callback
    if (saved && contextual_editing_callbacks.onsave) {
      contextual_editing_callbacks.onsave(contextual_entry_id);
    } else if (!saved && contextual_editing_callbacks.oncancel) {
      contextual_editing_callbacks.oncancel(contextual_entry_id);
    }

    contextual_entry_id = undefined;
    contextual_editing_callbacks = { onsave: null, oncancel: null };
    // Note: Scroll restoration is handled by BaseDrawer
  }

  /**
   * Start contextual editing for an entry
   * @param {string} entry_id - The entry ID to edit
   * @param {{ onsave?: (entry_id: string) => void, oncancel?: (entry_id: string) => void }} options
   */
  function start_contextual_editing(entry_id, options = {}) {
    // Note: Scroll position is saved by BaseDrawer when it mounts
    contextual_entry_id = entry_id;
    contextual_editing_callbacks = {
      onsave: options.onsave || null,
      oncancel: options.oncancel || null
    };
  }

  /**
   * Open entry picker drawer
   * @param {{
   *   entry_type?: string,
   *   initial_entry_type?: string,
   *   initial_selected?: string[],
   *   excluded_ids?: string[],
   *   onconfirm?: (selected_ids: string[]) => void,
   *   oncancel?: () => void,
   * }} options
   */
  function open_entry_picker(options = {}) {
    entry_picker_options = {
      entry_type: options.entry_type || '',
      initial_entry_type: options.initial_entry_type || '',
      initial_selected: options.initial_selected || [],
      excluded_ids: options.excluded_ids || [],
      onconfirm: options.onconfirm || null,
      oncancel: options.oncancel || null,
    };
    entry_picker_open = true;
  }

  /**
   * Close entry picker drawer
   */
  function close_entry_picker() {
    entry_picker_open = false;
    entry_picker_options = {
      entry_type: '',
      initial_entry_type: '',
      initial_selected: [],
      excluded_ids: [],
      onconfirm: null,
      oncancel: null,
    };
  }

  async function cancel_editing() {
    if (is_processing) return; // Prevent double-click

    // Placement rolls back with the draft; uploaded media stays in the library.
    if (has_unsaved_changes()) {
      const message = uploads_busy() || entry?.body
        ? `${t('discard_changes_confirmation')}\n\n${t('uploaded_images_stay_in_library')}`
        : t('discard_changes_confirmation');
      if (!confirm(message)) return;
    }
    clear_draft();

    is_processing = true; // Block interactions without showing "saving" indicator

    // HACK: We handle two edge cases here, where someone might have clicked "edit" or "duplicate" on /all
    // Cancel should take you back from where you were coming from.
    const try_history_back = page.url.searchParams.get('edit') || page.url.searchParams.get('from');
    if (try_history_back && window.history.length > 1) {
      is_processing = false;
      return history.back();
    }

    // Roll back: fetch the latest data from the server (in case another user edited the page)
    // and dispose the old model to clean up leaked effects before creating a fresh one.
    if (!is_create_mode) {
      await refreshAll();
      entry.dispose?.();
      entry = new ENTRIES[raw_entry.type].model(raw_entry); // reset to server data
      is_editing = false;
      clear_selection();
    } else {
      // HACK: when start_editing hasn't been called, we know we are in "create mode",
      // so cancel means "abort creation"
      // TODO: find a better way to handle this case
      goto('/');
    }
    is_processing = false;
  }

  async function save() {
    if (is_processing) return; // Prevent double-click

    // Save waits, visibly, for in-flight uploads: pending media isn't in the
    // multiref yet, so saving mid-upload would silently drop its placement.
    // Uploads are seconds (masters are client-resized); optimizing never blocks.
    if (uploads_busy()) {
      start_processing();
      await wait_for_uploads();
      end_processing();
    }

    const new_or_updated_entry = entry.to_json();

    if (site_settings.home_page_preview_ref ==  new_or_updated_entry.entry_id && new_or_updated_entry.visibility !== 'public') {
      alert('Sorry. The homepage must be public. Select a different home page first, before you can change the visibility of this entry.');
      return;
    }

    if (!page.data.current_user) {
      alert('Sorry. Only the admin can save changes.');
      return;
    }

    start_processing();

    try {
      if (new_or_updated_entry.entry_id) {
        // Optimistic concurrency: the server rejects with 409 when someone else
        // saved this entry since we loaded it.
        const saved = await fetch_json('POST', '/api/update-entry', {
          ...new_or_updated_entry,
          base_updated_at: raw_entry?.updated_at ?? null,
        });
        // Our own write moved the row on. Adopt the version we just wrote as the
        // new base — the success path never reloads, so without this the second
        // save of a session compares against the version the page was loaded
        // with and reports our own previous save as a foreign edit.
        if (saved?.updated_at) {
          if (raw_entry) raw_entry.updated_at = saved.updated_at;
          entry.updated_at = saved.updated_at; // what the island's "updated at" shows
        }
      } else {
        const new_entry = await fetch_json('POST', '/api/create-entry', new_or_updated_entry);
        clear_draft();
        // Mark the create as saved before leaving, or beforeNavigate takes the still-armed
        // create form for unsaved work and asks to discard it on the way to the new page.
        saved_snapshot = JSON.stringify(new_or_updated_entry);
        goto(entry_url(new_entry));
        end_processing();
        return;
      }
    } catch (err) {
      if (err?.status === 409) {
        end_processing();
        if (confirm(t('entry_changed_reload_confirm'))) {
          clear_draft();
          await refreshAll();
          entry.dispose?.();
          entry = new ENTRIES[raw_entry.type].model(raw_entry);
          is_editing = false;
          clear_selection();
        } else {
          // Cancel only: keep the local version and overwrite on the next save.
          // The reload path must NOT land here — it now holds the current
          // version, so its base check has to stay armed against the next writer.
          raw_entry.updated_at = null;
        }
        return;
      }
      if (err?.status === 403) {
        end_processing();
        alert(t('entry_save_not_allowed'));
        return;
      }
      console.error(err);
      end_processing();
      alert('There was an error. You can try again, but before that, please just copy and paste your article into a safe place.');
      return;
    }
    saved_snapshot = JSON.stringify(new_or_updated_entry);
    clear_draft();

    // Promote standalone event to series (has pattern but no series_id yet)
    const is_promote_to_series = new_or_updated_entry.series_pattern?.frequency
      && !new_or_updated_entry.series_id
      && new_or_updated_entry.entry_id;

    if (is_promote_to_series) {
      try {
        await fetch_json('POST', '/api/promote-to-series', {
          entry_id: new_or_updated_entry.entry_id,
          entry_json: new_or_updated_entry,
        });
      } catch (err) {
        console.error('Failed to create series occurrences:', err);
        end_processing();
        alert(err.message);
        return;
      }
      // Reload to pick up the new series_id and series state
      await refreshAll();
      entry.dispose?.();
      entry = new ENTRIES[raw_entry.type].model(raw_entry);
    }

    is_editing = false;
    clear_selection();
    end_processing();

    // For series events, offer to propagate changes to all future events
    if (new_or_updated_entry.series_id && new_or_updated_entry.entry_id) {
      let impact_count = 0;
      try {
        const impact = await fetch_json('POST', '/api/series-impact', {
          series_id: new_or_updated_entry.series_id,
          from_entry_id: new_or_updated_entry.entry_id,
        });
        impact_count = impact?.total_future_count || 0;
      } catch {
        impact_count = 0;
      }
      // Only show the dialog if there are other events in the series to update
      // (impact count includes the current event, so > 1 means others exist)
      if (impact_count > 1) {
        series_dialog_entry = new_or_updated_entry;
        series_dialog_impact_count = impact_count - 1;
      }
    }
  }

  // Provide ENTRIES via context to avoid circular import issues
  setContext("entry_types", ENTRIES);

  setContext("entry_api", {
    get is_contextual_editing() {
      return contextual_entry_id ? true : false;
    },
    get is_disabled() {
      return Boolean(contextual_entry_id || entry_picker_open);
    },
    get is_processing() {
      return is_processing;
    },
    get show_processing() {
      return show_processing;
    },
    get page_title() {
      return entry.preview.title;
    },
    get page_description() {
      return entry.preview.description;
    },
    get entry() {
      return entry;
    },
    get active_container() {
      return active_container;
    },
    set active_container(_active_container) {
      // we clear the selection of the other container, when switching
      active_container?.clear_selection();
      active_container = _active_container;
    },
    get is_editing() {
      return is_editing;
    },
    set is_editing(_is_editing) {
      is_editing = _is_editing;
    },
    get add_to_collection_dialog_open() {
      return add_to_collection_dialog_open;
    },
    set add_to_collection_dialog_open(_add_to_collection_dialog_open) {
      add_to_collection_dialog_open = _add_to_collection_dialog_open;
    },
    start_contextual_editing,
    open_entry_picker,
    close_entry_picker,
    clear_selection,
    cancel_editing,
    start_editing,
    save,
    // Canvas toolbar menu coordination
    get active_canvas_menu() { return active_canvas_menu; },
    set_active_canvas_menu: (id) => { active_canvas_menu = id; },
  });

  $effect.pre(() => {
    if (raw_entry.entry_id !== entry.entry_id) {
      entry.dispose?.();
      entry = new ENTRIES[raw_entry.type].model(raw_entry);
      add_to_collection_dialog_open = false;
      // Navigating to another entry reuses this component: the baseline and
      // draft of the previous entry must not carry over
      untrack(() => {
        if (is_editing) begin_draft_session();
      });
    }
  });
</script>

<svelte:window
  onbeforeunload={handle_beforeunload}
  onpaste={handle_window_paste}
  ondragover={handle_window_dragover}
  ondrop={handle_window_drop}
/>

<!-- Currently we fall back to the site description if no description could be extracted from the entry's content. -->
<SEO
  title={get_page_title()}
  description={entry.preview.description || site_settings.description}
  image={entry.preview.image}
  type={entry.type}
  author={site_settings.name}
  noindex={entry.exclude_from_search && site_settings.home_page_preview_ref != entry.entry_id}
/>

{@html `<style>:root{--first-block-background-color: ${first_block_background_color}}</style>`}

<div>
  <MainPageContent
    id={entry.type + '-' + entry.entry_id}
    {is_editing}
  >
    <!-- Keyed on the entry id: navigating between entries reuses this component
         instance (the model is swapped in place), which would otherwise carry
         per-instance UI state — an open registration form, half-typed guest
         rows — from one entry to the next. Remounting clears it. -->
    {#key entry.entry_id}
      {@const EntryTypeComponent = ENTRIES[entry.type].component}
      <EntryTypeComponent
        {additional_entry_data}
        bind:entry
        {is_editing}
      />
    {/key}
    {#snippet footer()}
      <Footer />
    {/snippet}
  </MainPageContent>
</div>

{#if contextual_entry_id}
  <ContextualEditingDrawer
    entry_id={contextual_entry_id}
    onclose={() => end_contextual_editing({ saved: false })}
    onsave={() => end_contextual_editing({ saved: true })}
    oncancel={() => end_contextual_editing({ saved: false })}
  />
{/if}

{#if entry_picker_open && entry_picker_drawer.component}
  <entry_picker_drawer.component
    entry_type={entry_picker_options.entry_type}
    initial_entry_type={entry_picker_options.initial_entry_type}
    initial_selected={entry_picker_options.initial_selected}
    excluded_ids={entry_picker_options.excluded_ids}
    onconfirm={(selected_ids) => {
      entry_picker_options.onconfirm?.(selected_ids);
      close_entry_picker();
    }}
    oncancel={() => {
      entry_picker_options.oncancel?.();
      close_entry_picker();
    }}
    user_role={page.data.current_user?.role || ''}
  />
{/if}

{#if add_to_collection_dialog_open && add_to_collection_dialog.component}
  <add_to_collection_dialog.component {entry} collections={page.data.collections} onclose={() => { add_to_collection_dialog_open = false; }} />
{/if}

{#if series_dialog_entry && series_save_dialog.component}
  <series_save_dialog.component
    entry_json={series_dialog_entry}
    impact_count={series_dialog_impact_count}
    onclose={() => { series_dialog_entry = null; }}
  />
{/if}
